import React from 'react';
import { Tag, Tooltip } from 'antd';
import { 
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  StopOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { StatusTagProps } from '../../../types/plan-sub-task';
import { SUBTASK_STATUS_CONFIG, STATUS_COLORS, STATUS_DISPLAY_NAMES } from '../../../types/plan-sub-task';

// Icon mapping function
const getStatusIcon = (iconName: string) => {
  const iconMap = {
    'ClockCircleOutlined': <ClockCircleOutlined />,
    'PlayCircleOutlined': <PlayCircleOutlined />,
    'SyncOutlined': <SyncOutlined />,
    'CheckCircleOutlined': <CheckCircleOutlined />,
    'StopOutlined': <StopOutlined />
  };
  return iconMap[iconName as keyof typeof iconMap];
};

/**
 * Status Tag Component
 * Displays subtask status with color coding, icons and overdue indicators
 * Following plan management design patterns
 */
const StatusTag: React.FC<StatusTagProps> = ({ 
  status, 
  overdueFlag, 
  size = 'default',
  showIcon = true
}) => {
  const { t } = useTranslation();
  // Get status configuration
  const config = SUBTASK_STATUS_CONFIG[status];
  const statusIcon = showIcon ? getStatusIcon(config.icon) : undefined;

  // Determine if we should show overdue indicator
  const showOverdueIndicator = overdueFlag && (status === 'Ongoing' || status === 'Finished');

  // Determine final color - use error color for overdue finished tasks
  const finalColor = overdueFlag && status === 'Finished' ? 'error' : config.color;

  // Tooltip title for overdue items
  const tooltipTitle = overdueFlag 
    ? (status === 'Finished' ? t('subtask.statusTag.taskCompleteButOverdue') : t('subtask.statusTag.taskOverdueNeedAction'))
    : undefined;

  // Get status text from translation
  const getStatusText = (status: string) => {
    const statusKey = status.toLowerCase();
    return t(`subtask.status.${statusKey}`);
  };

  const tagContent = (
    <Tag 
      color={finalColor}
      icon={statusIcon}
      style={{ 
        fontSize: size === 'small' ? '12px' : '14px',
        padding: size === 'small' ? '2px 6px' : '4px 8px',
        borderRadius: '4px',
        fontWeight: 500
      }}
    >
      {getStatusText(status)}
      {showOverdueIndicator && (
        <ExclamationCircleOutlined 
          style={{ 
            marginLeft: 4, 
            color: '#ff4d4f',
            fontSize: size === 'small' ? '10px' : '12px'
          }} 
        />
      )}
    </Tag>
  );

  // Return with tooltip only if needed
  return overdueFlag ? (
    <Tooltip title={tooltipTitle}>
      {tagContent}
    </Tooltip>
  ) : tagContent;
};

export default StatusTag;